import translations from "@/config/translations";
import useLangStore from "@/hooks/useLangStore";
import { MenuItem, Switch, TextField } from "@mui/material";
import { useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SizeState {
    titleAr: string;
    titleEn: string;
    showTitle: boolean;
    price: string;
    hasDiscount: boolean;
    discountType: "percentage" | "fixed";
    discountAmount: string;
}

export interface ProductSizePopupDetails {
    initialSize?: SizeState;
    onSave: (size: SizeState) => void;
}

interface Props {
    onClose: () => void;
    details?: ProductSizePopupDetails;
}

// ── MUI Styles ────────────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& fieldset legend": {
        textAlign: dir === "rtl" ? "right" : "left",
        marginLeft: dir === "rtl" ? "auto" : undefined,
        marginRight: dir === "rtl" ? "10x" : undefined,
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": {
                backgroundColor: "var(--color-orange)",
                opacity: 1,
                border: 0,
            },
        },
    },
    "& .MuiSwitch-thumb": {
        width: 16,
        height: 16,
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
    },
    "& .MuiSwitch-track": {
        borderRadius: "var(--radius-full)",
        backgroundColor: "var(--color-gray-01)",
        opacity: 1,
    },
};

// ── Component ─────────────────────────────────────────────────────────────────

function emptySize(): SizeState {
    return {
        titleAr: "",
        titleEn: "",
        showTitle: true,
        price: "",
        hasDiscount: false,
        discountType: "percentage",
        discountAmount: "",
    };
}

export default function ProductSizePopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];

    const isEdit = !!details?.initialSize;
    const [size, setSize] = useState<SizeState>(details?.initialSize ?? emptySize());

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");
    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    const setField = <K extends keyof SizeState>(key: K, value: SizeState[K]) => {
        setSize((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        details?.onSave(size);
        onClose();
    };

    return (
        <div className="product-size-popup">
            <div className="psp-header">
                <h2 className="psp-title">{isEdit ? t.productForm.editSize : t.productForm.addSizeTitle}</h2>
                <button type="button" className="psp-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <div className="psp-body">
                {/* Price */}
                <TextField
                    fullWidth
                    type="number"
                    label={t.productForm.sizePrice}
                    value={size.price}
                    onChange={(e) => setField("price", e.target.value)}
                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                    sx={textFieldSx}
                />

                {/* Title Section */}
                <div className="psp-section">
                    <div className="psp-section-header">
                        <span className="psp-section-title">{t.productForm.sizeTitle}</span>
                        <Switch checked={size.showTitle} onChange={(e) => setField("showTitle", e.target.checked)} sx={switchSx} />
                    </div>
                    {size.showTitle && (
                        <div className="psp-row">
                            <TextField
                                fullWidth
                                label={t.productForm.arabic}
                                value={size.titleAr}
                                onChange={(e) => setField("titleAr", e.target.value)}
                                sx={textFieldSxRtl}
                            />
                            <TextField
                                fullWidth
                                label={t.productForm.english}
                                value={size.titleEn}
                                onChange={(e) => setField("titleEn", e.target.value)}
                                sx={textFieldSxLtr}
                            />
                        </div>
                    )}
                </div>

                {/* Discount Section */}
                <div className="psp-section">
                    <div className="psp-section-header">
                        <div className="psp-section-icon">
                            <img src="/icons/inputs-group-icon.svg" alt="" width={17} height={17} />
                        </div>
                        <span className="psp-section-title">{t.productForm.discountSection}</span>
                        <Switch checked={size.hasDiscount} onChange={(e) => setField("hasDiscount", e.target.checked)} sx={switchSx} />
                    </div>
                    {size.hasDiscount && (
                        <div className="psp-row">
                            <TextField
                                select
                                fullWidth
                                label={t.productForm.discountType}
                                value={size.discountType}
                                onChange={(e) => setField("discountType", e.target.value as "percentage" | "fixed")}
                                sx={textFieldSx}
                            >
                                <MenuItem value="percentage">{t.productForm.discountPercentage}</MenuItem>
                                <MenuItem value="fixed">{t.productForm.discountFixed}</MenuItem>
                            </TextField>
                            <TextField
                                fullWidth
                                type="number"
                                label={t.productForm.discountAmount}
                                value={size.discountAmount}
                                onChange={(e) => setField("discountAmount", e.target.value)}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={textFieldSx}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="psp-footer">
                <button type="button" className="psp-btn psp-btn--cancel" onClick={onClose}>
                    {t.productForm.cancel}
                </button>
                <button type="button" className="psp-btn psp-btn--save" onClick={handleSave}>
                    {isEdit ? t.productForm.update : t.productForm.create}
                </button>
            </div>
        </div>
    );
}
